import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Eye, EyeOff, Loader2, Vote, ArrowUp, Edit3, CheckCircle2, X, GripVertical,
  Calendar, Tag, FileText, Send, Save, ArrowLeft, MessageSquarePlus,
} from "lucide-react";

import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const QUESTION_TYPES = [
  { value: "single_choice", label: "Multiple choice (pick one)" },
  { value: "multi_choice", label: "Multi-select (pick several)" },
  { value: "scale", label: "Rating scale" },
  { value: "text", label: "Open ended (free text)" },
];

const VISIBILITY_OPTIONS = [
  { value: "always", label: "Always visible to everyone" },
  { value: "after_vote", label: "Only after the member votes" },
  { value: "founder_only", label: "Private to the DHDH team" },
];

const TOPIC_SUGGESTIONS = [
  "General", "Wages & Compensation", "Education & CE", "Workplace Culture",
  "Clinical Practice", "Career Growth", "Burnout & Wellness", "Community Events",
  "Product Feedback", "Industry Trends",
];

const STATUS_TONE = {
  draft: "bg-[#F0ECF7] text-[#6B4E8A]",
  active: "bg-[#8DB5A0]/15 text-[#3F7C5C]",
  closed: "bg-[#FAF7F2] text-[#94A3B8]",
};

const emptyQuestion = () => ({
  question_id: undefined,
  type: "single_choice",
  text: "",
  options: ["", ""],
  scale_min: 1,
  scale_max: 5,
  scale_min_label: "",
  scale_max_label: "",
  required: true,
  allow_other: false,
});

const emptyPoll = () => ({
  title: "",
  description: "",
  category: "",
  results_visibility: "after_vote",
  status: "draft",
  closes_at: "",
  source_suggestion_id: null,
  questions: [emptyQuestion()],
});

// ---------------------------------------------------------------------------
// Preview pane — mirrors the member voting view
// ---------------------------------------------------------------------------

const PollPreview = ({ draft }) => (
  <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-2xl p-5 space-y-4" data-testid="poll-preview">
    <div className="flex items-center gap-2 text-xs text-[#94A3B8] uppercase tracking-wider">
      <Eye size={12} /> Live preview · how members will see it
    </div>
    <div>
      {draft.category && (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#6B4E8A]/10 text-[#6B4E8A] mb-2">
          <Tag size={10} /> {draft.category}
        </span>
      )}
      <h3 className="text-lg font-semibold text-[#6B4E8A]">{draft.title || "Untitled poll"}</h3>
      {draft.description && <p className="text-sm text-[#64748B] mt-1">{draft.description}</p>}
      {draft.closes_at && (
        <p className="text-xs text-[#94A3B8] mt-1">
          Closes {new Date(draft.closes_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
        </p>
      )}
    </div>
    {draft.questions.map((q, qi) => (
      <div key={qi} className="bg-white border border-[#E5DFEC] rounded-xl p-4">
        <p className="font-medium text-[#6B4E8A]">
          {qi + 1}. {q.text || <span className="italic text-[#94A3B8]">Untitled question</span>}
          {q.required && <span className="text-[#DC2626] ml-1">*</span>}
        </p>
        {q.type === "single_choice" && (
          <div className="mt-3 space-y-2">
            {q.options.filter(Boolean).map((opt, oi) => (
              <label key={oi} className="flex items-center gap-2 px-3 py-2 border border-[#E5DFEC] rounded-lg text-sm text-[#6B4E8A]">
                <input type="radio" disabled className="accent-[#6B4E8A]" />
                {opt}
              </label>
            ))}
            {q.allow_other && (
              <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-[#6B4E8A]/40 rounded-lg text-sm text-[#6B4E8A] italic">
                <input type="radio" disabled className="accent-[#6B4E8A]" />
                Other (free text)
              </label>
            )}
          </div>
        )}
        {q.type === "multi_choice" && (
          <div className="mt-3 space-y-2">
            {q.options.filter(Boolean).map((opt, oi) => (
              <label key={oi} className="flex items-center gap-2 px-3 py-2 border border-[#E5DFEC] rounded-lg text-sm text-[#6B4E8A]">
                <input type="checkbox" disabled className="accent-[#6B4E8A]" />
                {opt}
              </label>
            ))}
            {q.allow_other && (
              <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-[#6B4E8A]/40 rounded-lg text-sm text-[#6B4E8A] italic">
                <input type="checkbox" disabled className="accent-[#6B4E8A]" />
                Other (free text)
              </label>
            )}
          </div>
        )}
        {q.type === "scale" && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {Array.from({ length: (q.scale_max || 5) - (q.scale_min || 1) + 1 }, (_, i) => (q.scale_min || 1) + i).map((n) => (
              <span key={n} className="w-10 h-10 rounded-xl border border-[#E5DFEC] flex items-center justify-center text-[#6B4E8A] font-semibold">{n}</span>
            ))}
            {(q.scale_min_label || q.scale_max_label) && (
              <div className="w-full flex justify-between text-xs text-[#94A3B8] mt-1">
                <span>{q.scale_min_label || ""}</span>
                <span>{q.scale_max_label || ""}</span>
              </div>
            )}
          </div>
        )}
        {q.type === "text" && (
          <Textarea disabled placeholder="Member typing area…" className="mt-3 bg-white" rows={2} />
        )}
      </div>
    ))}
    {draft.questions.length === 0 && (
      <p className="text-sm text-[#94A3B8] italic text-center py-4">No questions yet.</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Poll builder dialog — full form
// ---------------------------------------------------------------------------

export const PollBuilder = ({ initial, onSave, onClose, suggestion }) => {
  const [draft, setDraft] = useState(() => {
    if (initial) return { ...emptyPoll(), ...JSON.parse(JSON.stringify(initial)) };
    const base = emptyPoll();
    if (suggestion) {
      base.title = suggestion.title || "";
      base.description = suggestion.description || "";
      base.source_suggestion_id = suggestion.suggestion_id;
    }
    return base;
  });
  const [mode, setMode] = useState("edit"); // edit | preview
  const [saving, setSaving] = useState(false);

  const updateQ = (idx, patch) => {
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    }));
  };

  const moveQ = (idx, dir) => {
    setDraft((d) => {
      const arr = [...d.questions];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...d, questions: arr };
    });
  };

  const addQ = () => setDraft((d) => ({ ...d, questions: [...d.questions, emptyQuestion()] }));
  const removeQ = (idx) => setDraft((d) => ({ ...d, questions: d.questions.filter((_, i) => i !== idx) }));

  const updateOption = (qIdx, oIdx, val) => {
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, i) =>
        i === qIdx ? { ...q, options: q.options.map((o, j) => (j === oIdx ? val : o)) } : q
      ),
    }));
  };
  const addOption = (qIdx) => updateQ(qIdx, { options: [...draft.questions[qIdx].options, ""] });
  const removeOption = (qIdx, oIdx) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, i) =>
        i === qIdx ? { ...q, options: q.options.filter((_, j) => j !== oIdx) } : q
      ),
    }));

  const buildCleanPayload = (overrideStatus) => {
    const payload = {
      ...draft,
      title: draft.title.trim(),
      description: (draft.description || "").trim(),
      category: (draft.category || "").trim() || null,
      closes_at: draft.closes_at ? new Date(draft.closes_at).toISOString() : null,
      questions: draft.questions.map((q) => ({
        ...q,
        text: q.text.trim(),
        options: (q.options || []).map((o) => o.trim()).filter(Boolean),
      })),
    };
    if (overrideStatus) payload.status = overrideStatus;
    return payload;
  };

  const validate = (payload) => {
    if (!payload.title) { toast.error("Add a poll title."); return false; }
    if (payload.questions.length === 0) { toast.error("Add at least one question."); return false; }
    for (const q of payload.questions) {
      if (!q.text) { toast.error("Every question needs text."); return false; }
      if (["single_choice", "multi_choice"].includes(q.type) && q.options.length < 2) {
        toast.error("Choice questions need at least 2 options.");
        return false;
      }
    }
    return true;
  };

  const handleSave = async (targetStatus) => {
    const payload = buildCleanPayload(targetStatus);
    if (!validate(payload)) return;
    setSaving(true);
    try {
      await onSave(payload);
      onClose();
    } catch {
      // parent already surfaced the error; keep builder open
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="poll-builder-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle>{initial ? "Edit poll" : "Create a new poll"}</DialogTitle>
              <DialogDescription>
                Build your questions on the left, hit Preview to see exactly what members will see.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1 bg-[#F0ECF7] rounded-full p-1 shrink-0">
              <button
                type="button"
                onClick={() => setMode("edit")}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${mode === "edit" ? "bg-white text-[#6B4E8A] shadow-sm" : "text-[#94A3B8]"}`}
                data-testid="builder-mode-edit"
              >
                <Edit3 size={12} className="inline mr-1" /> Edit
              </button>
              <button
                type="button"
                onClick={() => setMode("preview")}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${mode === "preview" ? "bg-white text-[#6B4E8A] shadow-sm" : "text-[#94A3B8]"}`}
                data-testid="builder-mode-preview"
              >
                <Eye size={12} className="inline mr-1" /> Preview
              </button>
            </div>
          </div>
        </DialogHeader>

        {mode === "preview" ? (
          <div className="space-y-4">
            <PollPreview draft={draft} />
            <Button
              type="button"
              variant="outline"
              onClick={() => setMode("edit")}
              className="w-full"
              data-testid="back-to-edit-btn"
            >
              <ArrowLeft size={14} className="mr-2" /> Back to edit
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {suggestion && (
              <div className="bg-[#8DB5A0]/10 border border-[#8DB5A0]/30 rounded-lg p-3 text-xs text-[#3F7C5C]" data-testid="source-suggestion-banner">
                <MessageSquarePlus size={12} className="inline mr-1" />
                Crediting member suggestion <strong>&quot;{suggestion.title}&quot;</strong> by {suggestion.suggested_by_name || "a member"}.
              </div>
            )}

            <div>
              <Label htmlFor="poll-title">Poll title <span className="text-[#DC2626]">*</span></Label>
              <Input
                id="poll-title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                maxLength={200}
                placeholder="e.g. What's the biggest gap in your continuing education?"
                data-testid="poll-title-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="poll-category">
                  <Tag size={12} className="inline mr-1" /> Topic / category
                </Label>
                <Input
                  id="poll-category"
                  list="poll-category-suggestions"
                  value={draft.category || ""}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  placeholder="Pick or type a topic"
                  maxLength={80}
                  data-testid="poll-category-input"
                />
                <datalist id="poll-category-suggestions">
                  {TOPIC_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <Label htmlFor="poll-closes">
                  <Calendar size={12} className="inline mr-1" /> Expiry date (optional)
                </Label>
                <Input
                  id="poll-closes"
                  type="date"
                  value={draft.closes_at ? draft.closes_at.slice(0, 10) : ""}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDraft({ ...draft, closes_at: e.target.value })}
                  data-testid="poll-closes-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="poll-desc">
                <FileText size={12} className="inline mr-1" /> Description (optional)
              </Label>
              <Textarea
                id="poll-desc"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                maxLength={2000}
                rows={2}
                placeholder="Give members context for why this matters."
                data-testid="poll-description-input"
              />
            </div>

            <div>
              <Label>Results visibility</Label>
              <Select value={draft.results_visibility} onValueChange={(v) => setDraft({ ...draft, results_visibility: v })}>
                <SelectTrigger data-testid="poll-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Questions builder */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-[#6B4E8A]">
                  Questions ({draft.questions.length})
                </h4>
                <Button type="button" size="sm" onClick={addQ} variant="outline" data-testid="add-question-btn">
                  <Plus size={14} className="mr-1" /> Add question
                </Button>
              </div>
              {draft.questions.map((q, qi) => (
                <div key={qi} className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-4 space-y-3" data-testid={`question-${qi}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-0.5 pt-1 shrink-0">
                      <button type="button" onClick={() => moveQ(qi, -1)} disabled={qi === 0} className="text-[#94A3B8] hover:text-[#6B4E8A] disabled:opacity-30" title="Move up">
                        <GripVertical size={14} />
                      </button>
                      <span className="text-[10px] text-[#94A3B8] text-center">{qi + 1}</span>
                    </div>
                    <div className="flex-1 space-y-2 min-w-0">
                      <Input
                        placeholder="Question text"
                        value={q.text}
                        onChange={(e) => updateQ(qi, { text: e.target.value })}
                        maxLength={300}
                        data-testid={`question-text-${qi}`}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Select value={q.type} onValueChange={(v) => updateQ(qi, { type: v, options: v.includes("choice") ? (q.options.length ? q.options : ["", ""]) : q.options })}>
                          <SelectTrigger data-testid={`question-type-${qi}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {QUESTION_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 text-sm text-[#6B4E8A] cursor-pointer">
                          <Switch
                            checked={q.required}
                            onCheckedChange={(v) => updateQ(qi, { required: v })}
                            data-testid={`question-required-${qi}`}
                          />
                          Required
                        </label>
                      </div>
                      {["single_choice", "multi_choice"].includes(q.type) && (
                        <div className="space-y-2 pt-1">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <Input
                                placeholder={`Option ${oi + 1}`}
                                value={opt}
                                onChange={(e) => updateOption(qi, oi, e.target.value)}
                                data-testid={`option-input-${qi}-${oi}`}
                              />
                              <button
                                type="button"
                                onClick={() => removeOption(qi, oi)}
                                disabled={q.options.length <= 2}
                                className="text-[#94A3B8] hover:text-[#DC2626] disabled:opacity-30"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <Button type="button" size="sm" variant="ghost" onClick={() => addOption(qi)} data-testid={`add-option-${qi}`}>
                              <Plus size={14} className="mr-1" /> Add option
                            </Button>
                            <label className="flex items-center gap-2 text-xs text-[#6B4E8A] cursor-pointer">
                              <Switch
                                checked={q.allow_other}
                                onCheckedChange={(v) => updateQ(qi, { allow_other: v })}
                                data-testid={`allow-other-${qi}`}
                              />
                              Include &quot;Other&quot; with free text
                            </label>
                          </div>
                        </div>
                      )}
                      {q.type === "scale" && (
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <Input
                            type="number"
                            min={0}
                            max={9}
                            value={q.scale_min}
                            onChange={(e) => updateQ(qi, { scale_min: Number(e.target.value) || 1 })}
                            placeholder="Min"
                          />
                          <Input
                            type="number"
                            min={2}
                            max={10}
                            value={q.scale_max}
                            onChange={(e) => updateQ(qi, { scale_max: Number(e.target.value) || 5 })}
                            placeholder="Max"
                          />
                          <Input
                            value={q.scale_min_label || ""}
                            onChange={(e) => updateQ(qi, { scale_min_label: e.target.value })}
                            placeholder="Low end label (optional)"
                          />
                          <Input
                            value={q.scale_max_label || ""}
                            onChange={(e) => updateQ(qi, { scale_max_label: e.target.value })}
                            placeholder="High end label (optional)"
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQ(qi)}
                      disabled={draft.questions.length <= 1}
                      className="text-[#94A3B8] hover:text-[#DC2626] disabled:opacity-30 pt-1 shrink-0"
                      title="Remove question"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} className="sm:mr-auto" data-testid="cancel-builder-btn">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => setMode(mode === "preview" ? "edit" : "preview")}
            disabled={saving}
            data-testid="toggle-preview-btn"
          >
            <Eye size={14} className="mr-1.5" /> {mode === "preview" ? "Edit" : "Preview"}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave("draft")}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-1.5" />}
            Save draft
          </Button>
          <Button
            onClick={() => handleSave("active")}
            disabled={saving}
            className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white"
            data-testid="publish-poll-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-1.5" />}
            {initial && initial.status === "active" ? "Save & keep live" : "Publish now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Admin panel
// ---------------------------------------------------------------------------

const PollsAdminPanel = () => {
  const [polls, setPolls] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editing, setEditing] = useState(null);
  const [launchFromSuggestion, setLaunchFromSuggestion] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [pollsRes, sugRes] = await Promise.allSettled([
        axios.get(`${API}/admin/polls`),
        axios.get(`${API}/polls/suggestions`),
      ]);
      if (pollsRes.status === "fulfilled") setPolls(pollsRes.value.data);
      if (sugRes.status === "fulfilled") setSuggestions(sugRes.value.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const createPoll = async (payload) => {
    try {
      await axios.post(`${API}/admin/polls`, payload);
      toast.success(payload.status === "active" ? "Poll published — members can vote now." : "Poll saved as draft.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create poll.");
      throw err;
    }
  };
  const updatePoll = async (poll, patch) => {
    try {
      await axios.put(`${API}/admin/polls/${poll.poll_id}`, patch);
      toast.success("Poll updated.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update poll.");
    }
  };
  const editPollFull = async (poll, payload) => {
    const url = `${API}/polls/${poll.poll_id}`;
    try {
      try {
        await axios.put(url, payload);
      } catch (err) {
        const d = err.response?.data?.detail;
        if (err.response?.status !== 409 || d?.code !== "confirm_required") throw err;
        if (!window.confirm(`Founder override: ${d.message}`)) throw new Error("cancelled");
        await axios.put(url, { ...payload, confirm_override: true });
      }
      toast.success("Poll updated.");
      await loadAll();
    } catch (err) {
      if (err.message !== "cancelled") {
        const d = err.response?.data?.detail;
        toast.error((typeof d === "string" ? d : d?.message) || "Couldn't update poll.");
      }
      throw err;
    }
  };
  const deletePoll = async (poll) => {
    if (!window.confirm(`Delete poll "${poll.title}" and all its responses? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/polls/${poll.poll_id}`);
      toast.success("Poll deleted.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete poll.");
    }
  };
  const updateSuggestion = async (sug, patch) => {
    try {
      await axios.put(`${API}/admin/polls/suggestions/${sug.suggestion_id}`, patch);
      toast.success("Suggestion updated.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update suggestion.");
    }
  };
  const deleteSuggestion = async (sug) => {
    if (!window.confirm("Permanently delete this suggestion?")) return;
    try {
      await axios.delete(`${API}/polls/suggestions/${sug.suggestion_id}`);
      toast.success("Suggestion deleted.");
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete suggestion.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Polls & Suggestions
        </h3>
        <Button
          onClick={() => { setEditing(null); setLaunchFromSuggestion(null); setShowBuilder(true); }}
          className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white rounded-full"
          data-testid="new-poll-btn"
        >
          <Plus size={14} className="mr-1.5" /> Create new poll
        </Button>
      </div>

      <Tabs defaultValue="polls" className="space-y-4">
        <TabsList className="bg-[#F0ECF7] p-1.5 rounded-xl">
          <TabsTrigger value="polls" className="data-[state=active]:bg-white px-3 py-1.5 rounded-lg" data-testid="admin-polls-tab">
            Polls ({polls.length})
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="data-[state=active]:bg-white px-3 py-1.5 rounded-lg" data-testid="admin-suggestions-tab">
            Suggestions ({suggestions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="polls" className="space-y-3">
          {loading ? (
            <div className="text-center py-10"><Loader2 size={20} className="animate-spin mx-auto text-[#6B4E8A]" /></div>
          ) : polls.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#94A3B8] border border-dashed border-[#E5DFEC] rounded-xl">
              No polls yet. Create one from scratch or launch a popular suggestion.
            </div>
          ) : (
            polls.map((p) => (
              <div key={p.poll_id} className="bg-white border border-[#E5DFEC] rounded-xl p-4" data-testid={`admin-poll-${p.poll_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-[#6B4E8A]">{p.title}</h4>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONE[p.status] || ""}`}>
                        {p.status}
                      </span>
                      {p.category && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#6B4E8A]/10 text-[#6B4E8A]">
                          {p.category}
                        </span>
                      )}
                      <span className="text-xs text-[#94A3B8]">{p.total_responses || 0} responses</span>
                    </div>
                    {p.description && <p className="text-sm text-[#64748B] mt-1 line-clamp-2">{p.description}</p>}
                    <p className="text-xs text-[#94A3B8] mt-1">
                      {p.questions?.length || 0} question{p.questions?.length === 1 ? "" : "s"} · Results: {p.results_visibility}
                      {p.closes_at && ` · Closes ${new Date(p.closes_at).toLocaleDateString("en-CA")}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {p.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => updatePoll(p, { status: "active" })} data-testid={`publish-poll-${p.poll_id}`}>
                        <Eye size={12} className="mr-1" /> Publish
                      </Button>
                    )}
                    {p.status === "active" && (
                      <Button size="sm" variant="outline" onClick={() => updatePoll(p, { status: "closed" })} data-testid={`close-poll-${p.poll_id}`}>
                        <EyeOff size={12} className="mr-1" /> Close
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setShowBuilder(true); }} data-testid={`edit-poll-${p.poll_id}`}>
                      <Edit3 size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePoll(p)} className="text-[#DC2626]" data-testid={`delete-poll-${p.poll_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="suggestions" className="space-y-3">
          {loading ? (
            <div className="text-center py-10"><Loader2 size={20} className="animate-spin mx-auto text-[#6B4E8A]" /></div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#94A3B8] border border-dashed border-[#E5DFEC] rounded-xl">
              No member suggestions yet.
            </div>
          ) : (
            suggestions.map((s) => (
              <div key={s.suggestion_id} className="bg-white border border-[#E5DFEC] rounded-xl p-4" data-testid={`admin-suggestion-${s.suggestion_id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center w-12 shrink-0 bg-[#F0ECF7] rounded-lg py-1.5 text-[#6B4E8A]">
                    <ArrowUp size={16} />
                    <span className="font-semibold text-sm">{s.upvote_count || 0}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-[#6B4E8A]">{s.title}</h4>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#6B4E8A]">
                        {s.status}
                      </span>
                    </div>
                    {s.description && <p className="text-sm text-[#64748B] mt-1">{s.description}</p>}
                    <p className="text-xs text-[#94A3B8] mt-1">
                      by {s.suggested_by_name || "member"} · {new Date(s.created_at).toLocaleDateString("en-CA")}
                    </p>
                    {s.founder_note && (
                      <p className="text-xs text-[#6B4E8A] mt-2 bg-[#FAF7F2] border border-[#E5DFEC] rounded px-2 py-1">
                        Note: {s.founder_note}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {s.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => updateSuggestion(s, { status: "considered" })} data-testid={`consider-${s.suggestion_id}`}>
                            Mark as considered
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => {
                            setLaunchFromSuggestion(s); setEditing(null); setShowBuilder(true);
                          }} data-testid={`launch-${s.suggestion_id}`}>
                            <Vote size={12} className="mr-1" /> Launch as poll
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => updateSuggestion(s, { status: "rejected" })} className="text-[#DC2626]">
                            Reject
                          </Button>
                        </>
                      )}
                      {s.status === "considered" && (
                        <Button size="sm" variant="outline" onClick={() => {
                          setLaunchFromSuggestion(s); setEditing(null); setShowBuilder(true);
                        }} data-testid={`launch-${s.suggestion_id}`}>
                          <Vote size={12} className="mr-1" /> Launch as poll
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteSuggestion(s)} className="text-[#DC2626] ml-auto">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>

      {showBuilder && (
        <PollBuilder
          initial={editing}
          suggestion={launchFromSuggestion}
          onClose={() => { setShowBuilder(false); setEditing(null); setLaunchFromSuggestion(null); }}
          onSave={(payload) => editing
            ? editPollFull(editing, payload)
            : createPoll(payload)
          }
        />
      )}
    </div>
  );
};

export default PollsAdminPanel;
